<template>
  <div class="profile-announcement-editor">
    <h2>Profile Announcement Editor</h2>
    
    <!-- Existing Announcements List -->
    <div v-if="loadingAnnouncements">Loading announcements...</div>
    <div v-else>
      <h3>Existing Announcements</h3>
      <ul>
        <li v-for="announcement in announcements" :key="announcement.id">
          <strong>{{ announcement.emoji }} - {{ announcement.description }}</strong>
          <br />
          Active: {{ announcement.activeState ? 'Yes' : 'No' }},
          Start: {{ formatDate(announcement.startDate) }},
          Expiry: {{ formatDate(announcement.expiryDate) }}
          <button @click="editAnnouncement(announcement)">Edit</button>
          <button @click="deleteAnnouncement(announcement.id)">Delete</button>
          <!-- Flag if this announcement overlaps with any other -->
          <span v-if="isOverlapping(announcement)" class="overlap-warning">⚠ Overlapping</span>
        </li>
      </ul>
    </div>

    <!-- Form for Adding / Editing Announcement -->
    <div class="announcement-form">
      <h3>{{ editingAnnouncement ? 'Edit Announcement' : 'Add Announcement' }}</h3>
      <form @submit.prevent="saveAnnouncement">
        <label>
          Emoji:
          <input type="text" v-model="form.emoji" required />
        </label>
        <label>
          Description:
          <textarea v-model="form.description" required></textarea>
        </label>
        <label>
          Button Active:
          <input type="checkbox" v-model="form.buttonState" />
        </label>
        <label>
          Button Link:
          <input type="text" v-model="form.buttonLink" />
        </label>
        <label>
          Button Text:
          <input type="text" v-model="form.buttonText" />
        </label>
        <label>
          Active Announcement:
          <input type="checkbox" v-model="form.activeState" />
        </label>
        <label>
          Start Date:
          <input type="datetime-local" v-model="form.startDate" required />
        </label>
        <label>
          Expiry Date:
          <input type="datetime-local" v-model="form.expiryDate" required />
        </label>
        <button type="submit">{{ editingAnnouncement ? 'Update' : 'Add' }} Announcement</button>
        <button type="button" v-if="editingAnnouncement" @click="cancelEdit">Cancel</button>
      </form>
      <div v-if="overlapWarning" class="overlap-warning">
        Warning: The scheduled time overlaps with an existing announcement!
      </div>
      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<script>
import { db } from "../firebaseConfig";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, Timestamp } from "firebase/firestore";

export default {
  name: "ProfileAnnouncementEditor",
  data() {
    return {
      announcements: [],
      loadingAnnouncements: false,
      // Form for adding/editing announcement
      form: {
        emoji: '',
        description: '',
        buttonState: false,
        buttonLink: '',
        buttonText: '',
        activeState: false,
        startDate: '',
        expiryDate: ''
      },
      editingAnnouncement: null,
      errorMessage: '',
      overlapWarning: false
    };
  },
  created() {
    this.fetchAnnouncements();
  },
  methods: {
  async fetchAnnouncements() {
    this.loadingAnnouncements = true;
    try {
      const announcementsRef = collection(db, "profileAnnouncement");
      const querySnapshot = await getDocs(announcementsRef);
      this.announcements = querySnapshot.docs.map(docSnap => {
        return { id: docSnap.id, ...docSnap.data() };
      });
    } catch (err) {
      console.error("Error fetching announcements:", err);
      this.errorMessage = "Failed to load announcements.";
    } finally {
      this.loadingAnnouncements = false;
    }
  },
  formatDate(timestamp) {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  },
  // Utility: Check whether two intervals overlap
  intervalsOverlap(startA, endA, startB, endB) {
    return (startA <= endB && startB <= endA);
  },
  // Check if a given announcement overlaps with any other in our list.
  // First, it verifies that startDate and expiryDate are defined.
  isOverlapping(announcement) {
    if (!announcement.startDate || !announcement.expiryDate) {
      return false;
    }
    const startA = announcement.startDate.toDate
      ? announcement.startDate.toDate()
      : new Date(announcement.startDate);
    const endA = announcement.expiryDate.toDate
      ? announcement.expiryDate.toDate()
      : new Date(announcement.expiryDate);
    return this.announcements.some(a => {
      if (a.id === announcement.id) return false; // Skip self
      if (!a.startDate || !a.expiryDate) return false;
      const startB = a.startDate.toDate ? a.startDate.toDate() : new Date(a.startDate);
      const endB = a.expiryDate.toDate ? a.expiryDate.toDate() : new Date(a.expiryDate);
      return this.intervalsOverlap(startA, endA, startB, endB);
    });
  },
  // Check overlap for the current form data (used for warning)
  checkFormOverlap() {
    if (!this.form.startDate || !this.form.expiryDate) return false;
    const startNew = new Date(this.form.startDate);
    const endNew = new Date(this.form.expiryDate);
    const overlap = this.announcements.some(a => {
      if (this.editingAnnouncement && a.id === this.editingAnnouncement.id) return false;
      if (!a.startDate || !a.expiryDate) return false;
      const startExisting = a.startDate.toDate ? a.startDate.toDate() : new Date(a.startDate);
      const endExisting = a.expiryDate.toDate ? a.expiryDate.toDate() : new Date(a.expiryDate);
      return this.intervalsOverlap(startNew, endNew, startExisting, endExisting);
    });
    this.overlapWarning = overlap;
    return overlap;
  },
  async saveAnnouncement() {
    this.errorMessage = '';
    // Check if there is an overlap
    if (this.checkFormOverlap()) {
      this.errorMessage = "The scheduled time overlaps with an existing announcement.";
      return;
    }
    // Prepare payload; convert startDate and expiryDate strings to Firestore Timestamps.
    const payload = {
      emoji: this.form.emoji,
      description: this.form.description,
      buttonState: this.form.buttonState,
      buttonLink: this.form.buttonLink,
      buttonText: this.form.buttonText,
      activeState: this.form.activeState,
      startDate: Timestamp.fromDate(new Date(this.form.startDate)),
      expiryDate: Timestamp.fromDate(new Date(this.form.expiryDate))
    };
    try {
      if (this.editingAnnouncement) {
        // Update existing announcement.
        const docRef = doc(db, "profileAnnouncement", this.editingAnnouncement.id);
        await updateDoc(docRef, payload);
      } else {
        // Add new announcement; add createdBy and createdAt.
        payload.createdBy = "currentUserID"; // Replace with actual UID
        payload.createdAt = Timestamp.now();
        await addDoc(collection(db, "profileAnnouncement"), payload);
      }
      // Reload announcements and reset form after saving.
      await this.fetchAnnouncements();
      this.resetForm();
    } catch (err) {
      console.error("Error saving announcement:", err);
      this.errorMessage = "Failed to save announcement.";
    }
  },
  editAnnouncement(announcement) {
  this.editingAnnouncement = announcement;
  this.form.emoji = announcement.emoji || '';
  this.form.description = announcement.description || '';
  this.form.buttonState = announcement.buttonState || false;
  this.form.buttonLink = announcement.buttonLink || '';
  this.form.buttonText = announcement.buttonText || '';
  this.form.activeState = announcement.activeState || false;

  // Safely convert startDate to an ISO string for datetime-local input
  if (announcement.startDate) {
    this.form.startDate = announcement.startDate.toDate 
      ? announcement.startDate.toDate().toISOString().slice(0, 16)
      : new Date(announcement.startDate).toISOString().slice(0, 16);
  } else {
    this.form.startDate = '';
  }

  // Safely convert expiryDate to an ISO string for datetime-local input
  if (announcement.expiryDate) {
    this.form.expiryDate = announcement.expiryDate.toDate 
      ? announcement.expiryDate.toDate().toISOString().slice(0, 16)
      : new Date(announcement.expiryDate).toISOString().slice(0, 16);
  } else {
    this.form.expiryDate = '';
  }
},
    cancelEdit() {
      this.editingAnnouncement = null;
      this.resetForm();
    },
    resetForm() {
      this.form = {
        emoji: '',
        description: '',
        buttonState: false,
        buttonLink: '',
        buttonText: '',
        activeState: false,
        startDate: '',
        expiryDate: ''
      };
      this.errorMessage = '';
      this.overlapWarning = false;
    },
    async deleteAnnouncement(id) {
      try {
        await deleteDoc(doc(db, "profileAnnouncement", id));
        await this.fetchAnnouncements();
      } catch (err) {
        console.error("Error deleting announcement:", err);
        this.errorMessage = "Failed to delete announcement.";
      }
    }
  },
  watch: {
    'form.startDate': function() {
      this.checkFormOverlap();
    },
    'form.expiryDate': function() {
      this.checkFormOverlap();
    }
  }
};
</script>

<style scoped>
.profile-announcement-editor {
  border: 1px solid #ddd;
  padding: 1rem;
  margin: 1rem 0;
  border-radius: 4px;
  background-color: #fff;
}
.announcement-form label {
  display: block;
  margin-bottom: 0.5rem;
}
.announcement-form input[type="text"],
.announcement-form textarea,
.announcement-form input[type="datetime-local"] {
  width: 100%;
  padding: 0.4rem;
  margin-bottom: 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.announcement-form button {
  padding: 0.5rem 1rem;
  border: none;
  background-color: #007bff;
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 0.5rem;
}
.announcement-form button:hover {
  background-color: #0056b3;
}
.overlap-warning {
  color: red;
  font-weight: bold;
  margin-top: 0.5rem;
}
.error-message {
  color: red;
  font-weight: bold;
  margin-top: 0.5rem;
}
</style>
